import type {FabricObject} from '@postermywall/fabricjs-2';
import {Rect, util} from '@postermywall/fabricjs-2';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {PageTransitions} from '@PosterWhiteboard/page/transitions/page-transitions.class';
import type {DipToColorTransitionObject, ItemTransitionObject, SlideTransitionObject, WipeTransitionObject} from '@PosterWhiteboard/page/transitions/page-transitions.types';
import {ItemTransitionType, SlideTransitionDirection} from '@PosterWhiteboard/page/transitions/page-transitions.types';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import {FULL_PAGE_OFFSET, FULL_PAGE_PADDING} from '@PosterWhiteboard/page/page.types';
import {rgbaArrayToRgbString} from '@Utils/color.util';
import {assertNever} from '@Utils/assert-never.util';

export interface SavedFabricState {
  opacity: number;
  left: number;
  top: number;
  clipPath: FabricObject['clipPath'];
}

const DIP_MIDPOINT = 0.5;

/**
 * Applies transition effects to background fabric objects as a pure function of page time.
 * Driven from `Page.onPageTimeUpdated` (every clock tick AND every seek — unlike item
 * animations, transitions render their intermediate frames while scrubbing paused, matching
 * generation output exactly) and from `PosterVideoGeneration` per frame.
 *
 * Effects write only to fabric objects, never to item class state — serialization and history
 * read `toObject()`, so a snapshot taken mid-transition can never capture displaced values.
 * The resting fabric state is captured on first touch per window and restored on exit.
 */
export class PageTransitionsRenderer {
  #savedStatesByItemUid = new Map<string, SavedFabricState>();
  #dipOverlay: Rect | null = null;
  readonly #transitions: PageTransitions;
  readonly #page: Page;

  /**
   * Ergonomic brand check guarding the resting-state accessors: the item-duplicate flow
   * (SelectionManager.duplicateSelection) lodash-cloneDeeps a live item, and the clone's
   * page graph includes a STRUCTURAL copy of this renderer — right prototype, but private
   * fields only a real constructor call installs. Such a copy displaces nothing, so the
   * accessors answer "no resting state" instead of throwing the private-brand TypeError.
   * Static because an instance #method would carry the same brand and throw on the clone.
   */
  static #isRealInstance(candidate: PageTransitionsRenderer): boolean {
    return #savedStatesByItemUid in candidate;
  }

  public constructor(transitions: PageTransitions, page: Page) {
    this.#transitions = transitions;
    this.#page = page;
  }

  public setFrame(time: number): void {
    const affectedItemUids = new Set<string>();
    let isDipOverlayActive = false;
    let changed = false;

    for (const transition of Object.values(this.#transitions.transitionsHashMap)) {
      const window = this.#transitions.getWindowForTransition(transition);
      if (!window || time < window.startTime || time >= window.endTime) {
        continue;
      }
      const fromItem = this.#page.items.getItem(transition.fromItemUid);
      const toItem = this.#page.items.getItem(transition.toItemUid);
      if (!fromItem?.fabricObject || !toItem?.fabricObject) {
        continue;
      }

      const progress = (time - window.startTime) / (window.endTime - window.startTime);
      this.#applyTransitionFrame(transition, fromItem, toItem, progress, affectedItemUids);
      if (transition.type === ItemTransitionType.DIP_TO_COLOR) {
        isDipOverlayActive = true;
      }
      changed = true;
    }

    if (this.#restoreItemsOutsideActiveWindows(affectedItemUids)) {
      changed = true;
    }
    if (!isDipOverlayActive && this.#removeDipOverlay()) {
      changed = true;
    }
    if (changed) {
      this.#page.fabricCanvas.requestRenderAll();
    }
  }

  /**
   * The item's pre-transition resting fabric state while a transition frame currently
   * displaces it, undefined otherwise. Serialization reads through this so a snapshot
   * taken with the playhead inside a transition window captures resting values, never
   * displaced ones — keeping history diffs and saves honest.
   */
  public getRestingStateForItem(itemUid: string): Readonly<SavedFabricState> | undefined {
    if (!PageTransitionsRenderer.#isRealInstance(this)) {
      return undefined;
    }
    return this.#savedStatesByItemUid.get(itemUid);
  }

  /**
   * Applies a MODEL-driven write to an item this renderer currently displaces: the new values
   * land in the saved resting state — what window-exit restores and serialization reads — and
   * the next setFrame recomputes the effect from them. Writing raw fabric instead would get
   * visually reverted on the next frame and serialized stale. Returns false when the item
   * isn't displaced; the caller then writes fabric directly as usual.
   */
  public applyRestingStateUpdate(itemUid: string, update: Partial<Pick<SavedFabricState, 'left' | 'top' | 'opacity'>>): boolean {
    if (!PageTransitionsRenderer.#isRealInstance(this)) {
      return false;
    }
    const saved = this.#savedStatesByItemUid.get(itemUid);
    if (!saved) {
      return false;
    }
    if (update.left !== undefined) {
      saved.left = update.left;
    }
    if (update.top !== undefined) {
      saved.top = update.top;
    }
    if (update.opacity !== undefined) {
      saved.opacity = update.opacity;
    }
    return true;
  }

  /** Restores every touched fabric object and removes the overlay. For page disposal and teardown. */
  public reset(): void {
    const changed = this.#restoreItemsOutsideActiveWindows(new Set());
    if (this.#removeDipOverlay() || changed) {
      this.#page.fabricCanvas.requestRenderAll();
    }
  }

  #applyTransitionFrame(transition: ItemTransitionObject, fromItem: ItemType, toItem: ItemType, progress: number, affectedItemUids: Set<string>): void {
    switch (transition.type) {
      case ItemTransitionType.FADE:
        this.#applyFade(fromItem, toItem, progress, affectedItemUids);
        break;
      case ItemTransitionType.SLIDE:
        this.#applySlide(transition, fromItem, toItem, progress, affectedItemUids);
        break;
      case ItemTransitionType.WIPE:
        this.#applyWipe(transition, fromItem, toItem, progress, affectedItemUids);
        break;
      case ItemTransitionType.DIP_TO_COLOR:
        this.#applyDipToColor(transition, fromItem, toItem, progress);
        break;
      default:
        assertNever(transition);
    }
  }

  /**
   * Crossfade: only ONE of the pair's fabric objects animates opacity — the other stays fully
   * opaque beneath it, so the blend reads the same regardless of canvas stacking order.
   */
  #applyFade(fromItem: ItemType, toItem: ItemType, progress: number, affectedItemUids: Set<string>): void {
    const eased = util.ease.easeOutQuad(progress, 0, 1, 1);
    const {animatingItem, isIncoming} = this.#getAnimatingEndpoint(fromItem, toItem);
    const saved = this.#getOrSaveRestingState(animatingItem);
    this.#setFabricState(animatingItem, {opacity: saved.opacity * (isIncoming ? eased : 1 - eased)});
    affectedItemUids.add(animatingItem.uid);
  }

  /**
   * Fade and wipe animate exactly ONE endpoint of the pair — normally the topmost, so the
   * blend reads the same regardless of canvas stacking order. A visually transparent endpoint
   * (a TRANSPARENT background, or anything at opacity 0) breaks that equivalence: animating it
   * shows nothing, and the visible endpoint pops at the window edge instead of transitioning.
   * Animate the visible endpoint in that case.
   */
  #getAnimatingEndpoint(fromItem: ItemType, toItem: ItemType): {animatingItem: ItemType; isIncoming: boolean} {
    const isIncomingOnTop = this.#isIncomingOnTop(fromItem, toItem);
    const topItem = isIncomingOnTop ? toItem : fromItem;
    const bottomItem = isIncomingOnTop ? fromItem : toItem;
    if (this.#isVisuallyTransparent(topItem) && !this.#isVisuallyTransparent(bottomItem)) {
      return {animatingItem: bottomItem, isIncoming: !isIncomingOnTop};
    }
    return {animatingItem: topItem, isIncoming: isIncomingOnTop};
  }

  /**
   * A TRANSPARENT background renders nothing of its own; anything at opacity 0 AT REST is
   * equally invisible. Must read the RESTING opacity, never live fabric: a frame sampled at
   * exactly the window start writes eased(0) = 0 onto the incoming endpoint, and reading that
   * self-inflicted 0 on the next frame would flip the animating endpoint — restoring the
   * incoming item to full opacity for one frame (a flash in generated video, which samples
   * the window-start instant; playback ticks never land on progress 0 exactly).
   */
  #isVisuallyTransparent(item: ItemType): boolean {
    const restingOpacity = this.#savedStatesByItemUid.get(item.uid)?.opacity ?? item.fabricObject.opacity;
    return restingOpacity === 0 || item.isTransparentBackground();
  }

  /**
   * Slide is a PUSH: both backgrounds move together along `direction` — the outgoing slides
   * off-canvas while the incoming slides in from the opposite edge, tiled edge to edge, so
   * canvas stacking order doesn't matter. Motion is eased in-out for a soft launch and
   * landing; both items share the same eased progress, so the one-canvas-width tiling gap
   * holds at every instant.
   */
  #applySlide(transition: SlideTransitionObject, fromItem: ItemType, toItem: ItemType, progress: number, affectedItemUids: Set<string>): void {
    const offset = this.#getSlideEntryOffset(transition.direction);
    const eased = util.ease.easeInOutQuad(progress, 0, 1, 1);

    const savedFrom = this.#getOrSaveRestingState(fromItem);
    this.#setFabricState(fromItem, {
      left: savedFrom.left - offset.x * eased,
      top: savedFrom.top - offset.y * eased,
    });
    affectedItemUids.add(fromItem.uid);

    const savedTo = this.#getOrSaveRestingState(toItem);
    this.#setFabricState(toItem, {
      left: savedTo.left + offset.x * (1 - eased),
      top: savedTo.top + offset.y * (1 - eased),
    });
    affectedItemUids.add(toItem.uid);
  }

  /**
   * Wipe: a straight edge sweeps across in `direction`, revealing the incoming background —
   * neither background moves (unlike the slide push). Implemented by clipping ONE of the
   * pair's fabric objects: the incoming is clipped to the revealed region, or — when the
   * outgoing animates — the outgoing is clipped to the not-yet-wiped region, which
   * composites identically. The edge's sweep is eased in-out, same curve as slide.
   */
  #applyWipe(transition: WipeTransitionObject, fromItem: ItemType, toItem: ItemType, progress: number, affectedItemUids: Set<string>): void {
    const {animatingItem, isIncoming} = this.#getAnimatingEndpoint(fromItem, toItem);
    const eased = util.ease.easeInOutQuad(progress, 0, 1, 1);
    this.#getOrSaveRestingState(animatingItem);
    this.#setFabricState(animatingItem, {clipPath: this.#createWipeClipRect(transition.direction, isIncoming, eased)});
    affectedItemUids.add(animatingItem.uid);
  }

  /**
   * The clip region for the wipe's animating object, in absolute canvas coordinates. The edge
   * position sweeps the full canvas over the window; the incoming occupies the region behind
   * the edge (the side it entered from), the outgoing the region ahead of it. Bounds overflow
   * the canvas by 1px on the non-sweeping sides to hide sub-pixel seams (FULL_PAGE_* pattern).
   */
  #createWipeClipRect(direction: SlideTransitionDirection, isIncomingRegion: boolean, progress: number): Rect {
    const width = this.#page.poster.width;
    const height = this.#page.poster.height;
    let left = FULL_PAGE_OFFSET;
    let top = FULL_PAGE_OFFSET;
    let clipWidth = width + FULL_PAGE_PADDING;
    let clipHeight = height + FULL_PAGE_PADDING;

    switch (direction) {
      case SlideTransitionDirection.LEFT: {
        const edge = width * (1 - progress);
        left = isIncomingRegion ? edge : FULL_PAGE_OFFSET;
        clipWidth = isIncomingRegion ? width - edge + FULL_PAGE_PADDING : edge - FULL_PAGE_OFFSET;
        break;
      }
      case SlideTransitionDirection.RIGHT: {
        const edge = width * progress;
        left = isIncomingRegion ? FULL_PAGE_OFFSET : edge;
        clipWidth = isIncomingRegion ? edge - FULL_PAGE_OFFSET : width - edge + FULL_PAGE_PADDING;
        break;
      }
      case SlideTransitionDirection.UP: {
        const edge = height * (1 - progress);
        top = isIncomingRegion ? edge : FULL_PAGE_OFFSET;
        clipHeight = isIncomingRegion ? height - edge + FULL_PAGE_PADDING : edge - FULL_PAGE_OFFSET;
        break;
      }
      case SlideTransitionDirection.DOWN: {
        const edge = height * progress;
        top = isIncomingRegion ? FULL_PAGE_OFFSET : edge;
        clipHeight = isIncomingRegion ? edge - FULL_PAGE_OFFSET : height - edge + FULL_PAGE_PADDING;
        break;
      }
      default:
        assertNever(direction);
    }

    return new Rect({
      left,
      top,
      width: Math.max(0, clipWidth),
      height: Math.max(0, clipHeight),
      absolutePositioned: true,
    });
  }

  /**
   * Dip: a full-page color overlay above the pair ramps opaque to the boundary and back.
   * Neither background is touched, and neither needs visibility outside its own span — the
   * swap happens beneath a fully opaque overlay at the boundary.
   */
  #applyDipToColor(transition: DipToColorTransitionObject, fromItem: ItemType, toItem: ItemType, progress: number): void {
    const overlay = this.#getOrCreateDipOverlay();
    const opacity = progress < DIP_MIDPOINT ? progress / DIP_MIDPOINT : (1 - progress) / DIP_MIDPOINT;
    overlay.set({
      fill: rgbaArrayToRgbString(transition.color),
      opacity: Math.min(1, opacity),
    });
    const topPairIndex = Math.max(this.#page.items.getItemOrder(fromItem.uid), this.#page.items.getItemOrder(toItem.uid));
    if (topPairIndex >= 0) {
      this.#page.fabricCanvas.moveObjectTo(overlay, topPairIndex + 1);
    }
  }

  /** Where an incoming background starts, relative to rest, for a given motion direction. */
  #getSlideEntryOffset(direction: SlideTransitionDirection): {x: number; y: number} {
    const width = this.#page.poster.width;
    const height = this.#page.poster.height;
    switch (direction) {
      case SlideTransitionDirection.LEFT:
        return {x: width, y: 0};
      case SlideTransitionDirection.RIGHT:
        return {x: -width, y: 0};
      case SlideTransitionDirection.UP:
        return {x: 0, y: height};
      case SlideTransitionDirection.DOWN:
        return {x: 0, y: -height};
      default:
        return assertNever(direction);
    }
  }

  #isIncomingOnTop(fromItem: ItemType, toItem: ItemType): boolean {
    return this.#page.items.getItemOrder(toItem.uid) > this.#page.items.getItemOrder(fromItem.uid);
  }

  #getOrSaveRestingState(item: ItemType): SavedFabricState {
    let saved = this.#savedStatesByItemUid.get(item.uid);
    if (!saved) {
      saved = {
        opacity: item.fabricObject.opacity,
        left: item.fabricObject.left,
        top: item.fabricObject.top,
        clipPath: item.fabricObject.clipPath,
      };
      this.#savedStatesByItemUid.set(item.uid, saved);
    }
    return saved;
  }

  #setFabricState(item: ItemType, state: Partial<SavedFabricState>): void {
    item.fabricObject.set(state);
  }

  #restoreItemsOutsideActiveWindows(affectedItemUids: Set<string>): boolean {
    let changed = false;
    for (const [itemUid, saved] of this.#savedStatesByItemUid) {
      if (affectedItemUids.has(itemUid)) {
        continue;
      }
      const item = this.#page.items.getItem(itemUid);
      if (item?.fabricObject) {
        item.fabricObject.set({opacity: saved.opacity, left: saved.left, top: saved.top, clipPath: saved.clipPath});
      }
      this.#savedStatesByItemUid.delete(itemUid);
      changed = true;
    }
    return changed;
  }

  #getOrCreateDipOverlay(): Rect {
    if (!this.#dipOverlay) {
      this.#dipOverlay = new Rect({
        left: FULL_PAGE_OFFSET,
        top: FULL_PAGE_OFFSET,
        width: this.#page.poster.width + FULL_PAGE_PADDING,
        height: this.#page.poster.height + FULL_PAGE_PADDING,
        selectable: false,
        evented: false,
      });
      this.#page.fabricCanvas.add(this.#dipOverlay);
    }
    return this.#dipOverlay;
  }

  #removeDipOverlay(): boolean {
    if (!this.#dipOverlay) {
      return false;
    }
    this.#page.fabricCanvas.remove(this.#dipOverlay);
    this.#dipOverlay = null;
    return true;
  }
}
